import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Form,
} from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { dayOffApplicationsApiService } from '../../services/apiServices';
import { DayOffApplicationResponse, DayOffApplicationType } from '../../api-client';
import {
  getDayOffApplicationStatusLabel,
  getDayOffApplicationStatusBadgeVariant,
  getDayOffApplicationTypeLabel,
  getDayOffApplicationTypeBadgeVariant,
} from '../utils/statusConverters';
import DayOffCreateForm from '../components/DayOffCreateForm';
import { SmartPagination } from '../../components/SmartPagination';

const LIMIT = 10;

const MyDayOffPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState<DayOffApplicationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const typeFilter = searchParams.get('type') || '';

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * LIMIT;
      const response = await dayOffApplicationsApiService.getMyApplications(
        (typeFilter as DayOffApplicationType) || undefined,
        LIMIT,
        offset
      );
      setApplications(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке заявок');
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleTypeFilterChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('type', value);
    } else {
      params.delete('type');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setCreateSuccess(true);
    loadApplications();
    setTimeout(() => setCreateSuccess(false), 3000);
  };

  const handleUploadFile = async (file: File) => {
    const response = await dayOffApplicationsApiService.uploadTempFile(file);
    return { fileUrl: response.data?.file_url || '' };
  };

  const handleGetTemplate = async (type: DayOffApplicationType) => {
    const response = await dayOffApplicationsApiService.getTemplate(type);
    return response.data?.file_url || '';
  };

  const handleCreateSubmit = async (
    type: DayOffApplicationType,
    fileUrl: string,
    startDate: string,
    endDate: string,
    reason?: string,
    fileName?: string
  ) => {
    await dayOffApplicationsApiService.createMyApplication(
      type,
      fileUrl,
      startDate,
      endDate,
      reason,
      fileName
    );
    handleCreateSuccess();
  };

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Мои заявки на выходной</h2>
            <Button variant="primary" onClick={() => setShowCreateForm(!showCreateForm)}>
              {showCreateForm ? 'Скрыть форму' : 'Новая заявка'}
            </Button>
          </div>

          {createSuccess && <Alert variant="success">Заявка успешно создана!</Alert>}

          {showCreateForm && (
            <Card className="mb-4">
              <Card.Header>Новая заявка на выходной</Card.Header>
              <Card.Body>
                <DayOffCreateForm
                  onSubmit={handleCreateSubmit}
                  onUploadFile={handleUploadFile}
                  onGetTemplate={handleGetTemplate}
                  onCancel={() => setShowCreateForm(false)}
                />
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={typeFilter}
                  onChange={(e) => handleTypeFilterChange(e.target.value)}
                >
                  <option value="">Все типы</option>
                  {Object.values(DayOffApplicationType).map((t) => (
                    <option key={t} value={t}>
                      {getDayOffApplicationTypeLabel(t)}
                    </option>
                  ))}
                </Form.Select>
                <Button variant="outline-secondary" size="sm" onClick={handleResetFilters}>
                  Сбросить
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : error ? (
                <Alert variant="danger">{error}</Alert>
              ) : applications.length === 0 ? (
                <p className="text-muted text-center py-4">Заявки не найдены</p>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Тип</th>
                        <th>Причина</th>
                        <th>Статус</th>
                        <th>Дата создания</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td>
                            <Badge bg={getDayOffApplicationTypeBadgeVariant(app.type)}>
                              {getDayOffApplicationTypeLabel(app.type)}
                            </Badge>
                          </td>
                          <td>{app.reason || '—'}</td>
                          <td>
                            <Badge bg={getDayOffApplicationStatusBadgeVariant(app.status)}>
                              {getDayOffApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleString()}</td>
                          <td>
                            <Link to={`/vacation/day-off/my/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <SmartPagination
                    currentPage={currentPage}
                    limit={LIMIT}
                    currentItemsCount={applications.length}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MyDayOffPage;
